import { commaSeparateNumber } from "@highway/mbs-utils";
import { add, isBefore } from "date-fns";
import isURL from "validator/lib/isURL";

const validEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const validPathRegex = /[./]/g;

export const isRequired = (message?: string) => (value: string | number | undefined) => ({
    isValid:
        typeof value === "string"
            ? value.trim() !== ""
            : Array.isArray(value)
            ? value.length > 0
            : value !== 0 && value !== undefined && value !== null,
    message: message || "Field is required",
});

export const isOneRequired =
    (otherValue?: string | number | undefined, message?: string) => (value: string | number | undefined) => {
        const isValid = isRequired()(value).isValid;
        const isValid2 = isRequired()(otherValue).isValid;
        return {
            isValid: isValid || isValid2,
            message: message || "Field is required",
        };
    };

export const isEmailArray = (values: string[]) =>
    values
        .map((v: string) => isEmail("Invalid email format")(v))
        .reduce(
            (sum, curr) => ({
                isValid: sum.isValid === false ? false : curr.isValid,
                message: curr.message,
            }),
            { isValid: true, message: "" },
        );

export const isEmail = (message?: string) => (value: string) => ({
    isValid: validEmailRegex.test(value),
    message: message || "Email is required",
});

export const isMultiEmails =
    (message?: string, max: number = 4) =>
    (value: string) => ({
        isValid:
            value?.replaceAll(" ", "").split(",").length <= max &&
            value
                ?.replaceAll(" ", "")
                .split(",")
                .every((subValue: string) => validEmailRegex.test(subValue) === true),
        message: message || `All entries must be emails (max of ${max})`,
    });

export const isMultiEmailsArray =
    (message?: string, max: number = 4) =>
    (value: string[]) => ({
        isValid:
            value.length <= max && value.every((subValue: string) => validEmailRegex.test(subValue) === true),
        message: message || `All entries must be emails (max of ${max})`,
    });

export const isMultiEmailsArrayNoMax = (message?: string) => (value: string[]) => ({
    isValid: value.every((subValue: string) => validEmailRegex.test(subValue) === true),
    message: message || "All entries must be emails",
});

export const maxLength = (number: number, message?: string) => (value: number | string) => ({
    isValid: `${value}`.length <= number,
    message: message || `${number} character cap`,
});

export const validPath = (message?: string) => (value: string) => ({
    isValid: !validPathRegex.test(value),
    message: message || "Invalid",
});

export const maxNumber = (number: number, message?: string, lower?: boolean) => (value: number) => ({
    isValid: lower ? value < number : value <= number,
    message: message || `Value cannot exceed ${commaSeparateNumber(number)}`,
});

export const minNumber = (number: number, message?: string, higher?: boolean) => (value: number) => ({
    isValid: higher ? value > number : value >= number,
    message: message || `A minimum of ${commaSeparateNumber(number)} is required`,
});

export const withinRange = (min: number, max: number, message?: string) => (value: number) => ({
    isValid: value >= min && value <= max,
    message: message || `Value outside valid range (${min} - ${max})`,
});

export const paidBeforeTodayMessages = {
    default: "Value needs to be lower, as the loan will be paid before today",
    fields: `"Additional Monthly Principal Payment" or "Lump-Sum" need to be lower, as the loan will be paid before today`,
};

export const isNotPaidBeforeToday =
    (schedule: any[], startDate: { month: number; year: number }, message: string) => () => ({
        isValid: (() => {
            const start = new Date(startDate.year, startDate.month - 1);
            const endDate = add(start, { months: schedule.length });

            return !isBefore(endDate, new Date());
        })(),
        message,
    });

export const isUrl = (message?: string) => (value: string) => ({
    isValid: value.startsWith("http://") || value.startsWith("https://"),
    message: message || "Invalid URL",
});

export const isUrl2 = (message?: string) => (value: string) => {
    const trimmed = value?.trim();

    return {
        isValid:
            trimmed === "" || // allow empty string
            isURL(trimmed), // same rules as @IsUrl()
        message: message || "Invalid URL",
    };
};

export const isDateAfter = (olderDate: Date | undefined) => (value: Date | undefined) => ({
    isValid: olderDate === undefined || value === undefined ? true : new Date(value) >= new Date(olderDate),
    message: "Invalid Date",
});

export const isValidDate = (message?: string) => (value: Date | undefined) => ({
    isValid: value instanceof Date && !isNaN(value.getTime()),
    message: message || "Invalid date",
});

export const isPhoneNumber = (message?: string) => (value: string | number | undefined) => {
    const phoneString = String(value).replace(/\D/g, ""); // Remove non-digits
    return {
        isValid: phoneString.length === 10,
        message: message || "Phone number must be 10 digits",
    };
};
